import { useCallback, useEffect, useMemo, useState } from 'react';
import unescapedText from 'I18n/utils/unescapedText';
import { EllipsesIcon } from 'foundations-assets/icons/components/EllipsesIcon';
import { SprocketIcon } from 'foundations-assets/icons/components/SprocketIcon';
import { Flex } from 'foundations-components/components/Flex';
import { View } from 'foundations-components/components/View';
import { DialogCloseButton } from 'foundations-components/transitional/components/DialogCloseButton';
import { IconHolder } from 'foundations-components/transitional/components/IconHolder';
import { Image } from 'foundations-components/transitional/components/Image';
import { Modal } from 'foundations-components/transitional/components/Modal';
import { Wizard, WizardHeaderWithOverview, WizardStep } from 'foundations-components/transitional/components/Wizard';
import { getFullUrl } from 'hubspot-url-utils';
import { getIsLocalDevelopment } from 'marketplace-ui-common/utils/urls';
import { useDetailsPageContext } from '../../context/DetailsPageContext';
import { HubSpotOAuthStep } from './HubSpotOAuthStep';
import { InstallSuccessStep } from './InstallSuccessStep';
import { ThirdPartyAuthStep } from './ThirdPartyAuthStep';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export function InstallWizard({
  appId,
  features,
  appSignInConfiguration,
  isPreview,
  isUIE,
  iconUrl,
  isUngatedForSecureInstallFlow,
  installUrl,
  listingId,
  name,
  oauthQueryParams,
  onClose,
  portalId
}) {
  const skipThirdPartyAuth = appSignInConfiguration === null || appSignInConfiguration === void 0 ? void 0 : appSignInConfiguration.includes('OMIT_OAUTH');
  const {
    trackInteraction,
    isEmbedded
  } = useDetailsPageContext();
  const incomingOAuthQueryParams = useMemo(() => new URLSearchParams(oauthQueryParams), [oauthQueryParams]);
  const [stepIndex, setStepIndex] = useState(0);
  const [redirectUri, setRedirectUri] = useState('');
  const [popUpWindow, setPopUpWindow] = useState(null);
  const [thirdPartyState, setThirdPartyState] = useState('');
  const [hiddenIframe, setHiddenIframe] = useState(null);
  const isLocalDevelopment = getIsLocalDevelopment();
  useEffect(() => {
    const handleMessages = event => {
      // check that message originated from the same origin
      if (event.origin !== getFullUrl(isLocalDevelopment ? 'local' : 'app')) return;
      if (event.data && event.data.type === 'INSTALL_SUCCESS') {
        if (popUpWindow) popUpWindow.close();
        if (hiddenIframe) {
          hiddenIframe.remove();
          setHiddenIframe(null);
        }
        // Complete step index depends on whether we skipped third party auth
        setStepIndex(skipThirdPartyAuth ? 1 : 2);
        trackInteraction({
          action: 'complete third party sign in'
        });
      }
      if (event.data && event.data.type === 'THIRD_PARTY_AUTH_STATE') {
        if (popUpWindow) popUpWindow.close();
        setThirdPartyState(event.data.state || '');
        setStepIndex(1); // Move to HubSpot OAuth step
        trackInteraction({
          action: 'complete third party auth state'
        });
      }
    };
    const shouldListenForMessages = stepIndex === 0 && popUpWindow || stepIndex === 1 && (popUpWindow || hiddenIframe);
    if (shouldListenForMessages) {
      window.addEventListener('message', handleMessages);
    }
    const shouldRemoveListener = stepIndex === 2;
    if (shouldRemoveListener) {
      window.removeEventListener('message', handleMessages);
    }
  }, [trackInteraction, popUpWindow, stepIndex, isLocalDevelopment, isUngatedForSecureInstallFlow, hiddenIframe, skipThirdPartyAuth]);

  // Cleanup iframe on unmount
  useEffect(() => {
    return () => {
      if (hiddenIframe) {
        hiddenIframe.remove();
      }
    };
  }, [hiddenIframe]);
  const renderedHeaderIcons = useMemo(() => /*#__PURE__*/_jsxs(Flex, {
    alignItems: "center",
    justifySelf: "center",
    marginBlockEnd: "400",
    children: [/*#__PURE__*/_jsx(Image, {
      src: iconUrl,
      blockSize: "64px",
      objectFit: "contain",
      responsive: false
    }), /*#__PURE__*/_jsx(IconHolder, {
      icon: EllipsesIcon,
      size: "1000",
      paddingBlock: "0",
      color: "icon-secondary-default"
    }), /*#__PURE__*/_jsx(IconHolder, {
      icon: SprocketIcon,
      size: "1000",
      padding: "0",
      color: "icon-secondary-default"
    })]
  }), [iconUrl]);
  const handlePopUpClick = useCallback(url => {
    if (popUpWindow && !popUpWindow.closed) {
      popUpWindow.focus();
      trackInteraction({
        action: 'focus third party sign in pop up'
      });
    } else {
      const windowObjReference = window.open(url, '_target', 'popup=true,width=600,height=800,top=200,left=200');
      setPopUpWindow(windowObjReference);
      trackInteraction({
        action: 'open third party sign in pop up'
      });
    }
  }, [trackInteraction, popUpWindow, setPopUpWindow]);
  const handleOAuthSuccess = useCallback(oauthRedirectUri => {
    if (isUngatedForSecureInstallFlow || skipThirdPartyAuth) {
      // Secure flow or skip third party auth: perform hidden iframe request and go to success
      setStepIndex(skipThirdPartyAuth ? 1 : 2); // Different step index based on total steps

      const url = new URL(oauthRedirectUri);
      url.searchParams.append('step', 'finalize');
      // When skipping third party auth, state is empty; otherwise use thirdPartyState
      url.searchParams.append('state', skipThirdPartyAuth ? '' : thirdPartyState);
      url.searchParams.append('returnUrl', `${getFullUrl(isLocalDevelopment ? 'local' : 'app')}/marketplace/${portalId}/listing/${listingId}/install-success`);

      // Create a hidden iframe to complete the final auth step
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = 'none';
      iframe.src = url.toString();
      document.body.appendChild(iframe);
      setHiddenIframe(iframe);
      trackInteraction({
        action: skipThirdPartyAuth ? 'initiate install final step (skipped third party auth)' : 'initiate secure install final step'
      });
    } else {
      // Legacy flow: proceed to third party auth step
      setStepIndex(1);
      setRedirectUri(oauthRedirectUri);
    }
  }, [skipThirdPartyAuth, isUngatedForSecureInstallFlow, thirdPartyState, trackInteraction, isLocalDevelopment, portalId, listingId]);

  // Get the redirect URI from the preview query params for the ThirdPartyAuthStep
  const previewRedirectUri = useMemo(() => {
    return isPreview ? incomingOAuthQueryParams.get('redirect_uri') || '' : '';
  }, [isPreview, incomingOAuthQueryParams]);
  const steps = useMemo(() => {
    const hubSpotStep = {
      name: unescapedText('detailsPage.install.hubSpotStep.name'),
      element: /*#__PURE__*/_jsx(HubSpotOAuthStep, {
        isPreview: isPreview,
        name: name,
        oauthQueryParams: oauthQueryParams,
        portalId: portalId,
        renderedHeaderIcons: renderedHeaderIcons,
        onOAuthSuccess: handleOAuthSuccess
      })
    };
    const thirdPartyStep = {
      name: unescapedText('detailsPage.install.thirdPartyStep.name', {
        name
      }),
      element: /*#__PURE__*/_jsx(ThirdPartyAuthStep, {
        isPreview: isPreview,
        name: name,
        listingId: listingId,
        portalId: portalId,
        redirectUri: isUngatedForSecureInstallFlow ? '' : isPreview ? previewRedirectUri : redirectUri,
        renderedHeaderIcons: renderedHeaderIcons,
        popUpWindow: popUpWindow,
        onPopUpClick: handlePopUpClick,
        isSecureFlow: isUngatedForSecureInstallFlow,
        installUrl: installUrl
      })
    };
    const completeStep = {
      name: unescapedText('detailsPage.install.completeStep.name'),
      element: /*#__PURE__*/_jsx(InstallSuccessStep, {
        appId: appId,
        features: features,
        isEmbedded: isEmbedded,
        isUIE: isUIE,
        name: name,
        portalId: portalId,
        renderedHeaderIcons: renderedHeaderIcons,
        onClose: onClose,
        listingId: listingId
      })
    };
    if (skipThirdPartyAuth) {
      // When skipping third party auth, only show HubSpot OAuth and complete steps
      return [hubSpotStep, completeStep];
    }
    return isUngatedForSecureInstallFlow ? [thirdPartyStep, hubSpotStep, completeStep] : [hubSpotStep, thirdPartyStep, completeStep];
  }, [skipThirdPartyAuth, features, isUngatedForSecureInstallFlow, isPreview, name, oauthQueryParams, portalId, renderedHeaderIcons, handleOAuthSuccess, listingId, previewRedirectUri, redirectUri, popUpWindow, handlePopUpClick, appId, isUIE, isEmbedded, onClose, installUrl]);
  return /*#__PURE__*/_jsxs(Modal, {
    use: "conversational",
    width: 800,
    size: "tall",
    centered: true,
    children: [/*#__PURE__*/_jsx(DialogCloseButton, {
      onClick: onClose
    }), /*#__PURE__*/_jsx(View, {
      marginBlockStart: "800",
      children: /*#__PURE__*/_jsx(Wizard, {
        headerComponent: WizardHeaderWithOverview,
        stepIndex: stepIndex,
        onStepIndexChange: e => {
          const index = +e.target.value;
          setStepIndex(index);
        },
        onReject: () => null,
        onConfirm: () => null,
        footerComponent: isPreview ? undefined : () => null,
        children: steps.map((step, index) => /*#__PURE__*/_jsx(WizardStep, {
          name: step.name,
          children: step.element
        }, index))
      })
    })]
  });
}